export type InquiryStatus =
  | "待处理"
  | "处理中"
  | "待填写"
  | "待支付"
  | "待使用"
  | "已完成"
  | "已取消"
  | "已退款"
  | "无效";

/** AI任务类型中文映射 */
const TASK_TYPE_LABEL: Record<string, string> = {
  book_train: "火车票",
  book_flight: "机票",
  book_hotel: "酒店",
  book_car: "租车",
  book_guide: "达人/导游",
  book_tour: "旅游线路",
  book_food: "餐饮预订",
  book_transfer: "接送服务",
};

/**
 * 将后端 order_status 数字码转为前端 InquiryStatus
 *
 * 后端完整状态：
 *   0=待处理，1=处理中(报价待确认)，2=用户信息待填写，
 *   5=待支付，6=支付成功待使用，10=完成，
 *   -1=取消，-3=退款，-5=无效
 */
export function mapOrderStatus(status: number): InquiryStatus {
  switch (status) {
    case 0:
      return "待处理";
    case 1:
      return "处理中";
    case 2:
      return "待填写";
    case 5:
      return "待支付";
    case 6:
      return "待使用";
    case 10:
      return "已完成";
    case -1:
      return "已取消";
    case -3:
      return "已退款";
    case -5:
      return "无效";
    default:
      return "待处理";
  }
}

export interface InquiryProductItem {
  type: "hotel" | "car" | "tour" | "guide";
  icon: string;
  label: string;
  name: string;
  spec: string;
  productId: string;
  originalPrice?: number;
  quotedPrice?: number;
}

export interface UserNeeds {
  destination: string;
  dates: string;
  travelers: number;
  budget: string;
  selfDrive: boolean;
  accommodation: string;
  activities: string;
  dining: string;
  otherNeeds: string;
}

export interface InquiryOrder {
  id: string;
  orderNo?: string;          // 接口 order_no（询单号）
  status: InquiryStatus;
  userPhone: string;
  userName: string;
  userAvatar: string;
  createTime: string;
  needs: UserNeeds;
  products: InquiryProductItem[];
  totalQuotedPrice?: number;
  depositAmount?: number;
  tailAmount?: number;
  convertedOrderId?: string;
  assignedTo?: string;
  aiSummary: string;
  staffNote?: string;
}

/** 页面视图状态 */
export type PageView = "list" | "detail" | "quote" | "payment";

// ── 状态配置 ──────────────────────────────────────────────
export const inquiryStatusConfig: Record<InquiryStatus, {
  label: string;
  color: string;
  bg: string;
  icon: string;
  desc: string;
  step: number;
}> = {
  "待处理": { label: "待处理", color: "#D97706", bg: "#FEF3C7", icon: "ri-time-line", desc: "您的需求询单已提交，专属顾问正在整理方案", step: 0 },
  "处理中": { label: "处理中", color: "#0891B2", bg: "#CFFAFE", icon: "ri-send-plane-line", desc: "顾问正在为您整理方案，请耐心等待报价", step: 1 },
  "待填写": { label: "待填写", color: "#7C3AED", bg: "#F5F3FF", icon: "ri-edit-box-line", desc: "请补充完善订单信息以便后续操作", step: 1 },
  "待支付": { label: "待支付", color: "#DC2626", bg: "#FEE2E2", icon: "ri-bank-card-line", desc: "您已确认方案，请完成支付以正式成单", step: 2 },
  "待使用": { label: "待使用", color: "#2563EB", bg: "#DBEAFE", icon: "ri-calendar-check-line", desc: "支付成功，等待出行使用", step: 3 },
  "已完成": { label: "已完成", color: "#059669", bg: "#D1FAE5", icon: "ri-checkbox-circle-line", desc: "订单已全部完成，感谢您的使用", step: 4 },
  "已取消": { label: "已取消", color: "#9CA3AF", bg: "#F3F4F6", icon: "ri-close-circle-line", desc: "该询单已被取消", step: -1 },
  "已退款": { label: "已退款", color: "#F59E0B", bg: "#FEF3C7", icon: "ri-refund-2-line", desc: "该询单已退款", step: -1 },
  "无效": { label: "无效", color: "#EF4444", bg: "#FEE2E2", icon: "ri-error-warning-line", desc: "该询单已失效", step: -1 },
};

export const inquirySteps = ["提交询单", "顾问报价", "确认支付", "成单出行"];

export const BRAND = "#FF6B35";

export const productTypeBg: Record<string, string> = {
  hotel: "bg-amber-50 text-amber-700 border-amber-100",
  car: "bg-sky-50 text-sky-700 border-sky-100",
  route: "bg-emerald-50 text-emerald-700 border-emerald-100",
  guide: "bg-purple-50 text-purple-700 border-purple-100",
  food: "bg-orange-50 text-orange-700 border-orange-100",
  transfer: "bg-blue-50 text-blue-700 border-blue-100",
};

// ── 接口数据 → UI 组件映射 ──────────────────────────────

import type { AiOrderItem } from "@/api/aiOrder";

/**
 * 将后端 AiOrderItem 转换为前端 InquiryOrder
 * 供列表卡片、详情页等组件消费
 */
export function mapAiOrderToInquiry(item: AiOrderItem): InquiryOrder {
  const tp = item.task_params ?? {};
  const status = mapOrderStatus(item.order_status);

  // 从 task_params 中提取需求信息
  const destination =
    tp.destination ||
    (tp as any).to_city ||
    (tp as any).city ||
    "";
  const departureDate =
    tp.departure_date ||
    tp.date ||
    tp.check_in ||
    tp.dinner_date ||
    tp.datetime ||
    "";
  const passengers = Number(tp.passengers || tp.travelers || tp.people || tp.guest_count || 1);
  const budget = String(tp.budget || tp.price_range || item.order_amount || item.pay_amount || 0);

  // 构建产品标签
  const taskLabel = TASK_TYPE_LABEL[item.task_type] || item.task_type;

  return {
    id: String(item.id),
    status,
    userPhone: "",
    userName: "",
    userAvatar: "",
    createTime: item.created_at,
    needs: {
      destination: destination || "未知目的地",
      dates: departureDate || "日期待定",
      travelers: passengers,
      budget: budget || "待报价",
      selfDrive: Boolean((tp as any).self_drive),
      accommodation: (tp as any).accommodation || "",
      activities: (tp as any).activities || "",
      dining: (tp as any).dining || "",
      otherNeeds: item.task_params_text || "",
    },
    products: [
      {
        type: (item.task_type.replace("book_", "") as any) || "tour",
        icon: getTaskIcon(item.task_type),
        label: taskLabel,
        name: taskLabel,
        spec: item.task_params_text || `${destination} · ${passengers}人`,
        productId: String(item.id),
        originalPrice: item.order_amount != null ? item.order_amount : undefined,
        quotedPrice: item.pay_amount != null ? item.pay_amount : undefined,
      },
    ],
    totalQuotedPrice:
      (item.pay_amount != null && item.pay_amount > 0)
        ? item.pay_amount
        : (item.order_amount != null ? item.order_amount : undefined),
    depositAmount: undefined,
    tailAmount: undefined,
    convertedOrderId: item.order_no || (item.order_id ? String(item.order_id) : undefined),
    orderNo: item.order_no || undefined,
    assignedTo: undefined,
    aiSummary: item.task_params_text || `任务类型：${taskLabel}`,
    staffNote: item.error_message ?? undefined,
  };
}

function getTaskIcon(taskType: string): string {
  const map: Record<string, string> = {
    book_train: "ri-train-line",
    book_flight: "ri-flight-takeoff-line",
    book_hotel: "ri-hotel-bed-line",
    book_car: "ri-car-line",
    book_guide: "ri-user-star-line",
    book_tour: "ri-map-pin-line",
    book_food: "ri-restaurant-line",
    book_transfer: "ri-car-door-line",
  };
  return map[taskType] || "ri-file-list-3-line";
}
